import { CheckboxField } from '~/components/checkbox'
import { MultiSelectField } from '~/components/multi-select'
import { SelectField } from '~/components/select'
import { TextInputField } from '~/components/text-input'
import { TextareaField } from '~/components/textarea'
import { CustomFieldType } from '~/types/graphql'

import { CustomFieldCheckboxGroup } from './CustomFieldCheckboxGroup'

// Above this count the checkboxes become a wall, so the list stays a dropdown.
const MAX_CHECKBOX_OPTIONS = 8

type Props = {
  name: string
  label: string
  type: CustomFieldType
  options?: string[] | null
  optional?: boolean
}

export const CustomFieldInput = ({
  name,
  label,
  type,
  options,
  optional,
}: Props) => {
  const selectOptions = (options ?? []).map(option => ({
    label: option,
    value: option,
  }))

  switch (type) {
    case CustomFieldType.Textarea:
      return (
        <TextareaField
          name={name}
          label={label}
          optional={optional}
          minRows={3}
        />
      )

    case CustomFieldType.Boolean:
      return <CheckboxField name={name} label={label} />

    case CustomFieldType.Select:
      return (
        <SelectField
          name={name}
          label={label}
          optional={optional}
          options={selectOptions}
          placeholder="Select an option"
        />
      )

    case CustomFieldType.MultiSelect:
      // Checkboxes show every choice at once, which a dropdown reading
      // "2 selected" cannot. Both store one row per selected value.
      if (options?.length && options.length <= MAX_CHECKBOX_OPTIONS) {
        return (
          <CustomFieldCheckboxGroup
            name={name}
            label={label}
            optional={optional}
            options={options}
          />
        )
      }

      return (
        <MultiSelectField
          name={name}
          label={label}
          optional={optional}
          options={selectOptions}
          placeholder="Select options"
        />
      )

    case CustomFieldType.Number:
      return (
        <TextInputField
          name={name}
          label={label}
          optional={optional}
          type="number"
        />
      )

    case CustomFieldType.Date:
      return (
        <TextInputField
          name={name}
          label={label}
          optional={optional}
          type="date"
        />
      )

    default:
      return <TextInputField name={name} label={label} optional={optional} />
  }
}
